import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {Animation|null} */
  #animation = null;
  #isClosing = false;
  #isOpening = false;

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement|null} */
  get content() {
    return this.details.querySelector('.details-content');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #disableAnimationOnDesktop() {
    return this.dataset.disableAnimationOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    if (isDesktop && this.#disableAnimationOnDesktop) {
      return;
    }

    event.preventDefault();

    if (this.#isClosing || !this.details.open) {
      this.open();
    } else if (this.#isOpening || this.details.open) {
      this.close();
    }
  };

  open() {
    const content = this.content;
    if (!content) {
      this.details.open = true;
      return;
    }

    if (this.details.open && !this.#isClosing && !this.#isOpening) {
      return;
    }

    if (this.#animation) {
      this.#animation.cancel();
    }

    const wasClosed = !this.details.open || this.#isClosing;
    const startHeight = wasClosed ? 0 : content.offsetHeight;

    this.#isClosing = false;
    this.#isOpening = true;

    this.details.removeAttribute('data-closing');

    // Clamp height and overflow BEFORE details gets open attribute to eliminate flicker
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';
    this.details.style.overflow = 'hidden';

    this.details.open = true;
    this.details.setAttribute('open', '');

    // Measure end height
    content.style.height = 'auto';
    const endHeight = content.offsetHeight;
    content.style.height = `${startHeight}px`;

    // Force reflow
    content.offsetHeight;

    const duration = 350;
    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

    this.#animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: startHeight === 0 ? 0 : 1, transform: startHeight === 0 ? 'translateY(-8px)' : 'translateY(0)' },
        { height: `${endHeight}px`, opacity: 1, transform: 'translateY(0)' }
      ],
      { duration, easing }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(true);
    this.#animation.oncancel = () => (this.#isOpening = false);
  }

  close() {
    const content = this.content;
    if (!content) {
      this.details.open = false;
      return;
    }

    if (this.#animation) {
      this.#animation.cancel();
    }

    this.#isClosing = true;
    this.#isOpening = false;

    this.details.setAttribute('data-closing', 'true');
    this.details.open = true;

    const startHeight = content.offsetHeight;
    const endHeight = 0;

    content.style.overflow = 'hidden';
    this.details.style.overflow = 'hidden';

    const duration = 300;
    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';

    this.#animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1, transform: 'translateY(0)' },
        { height: `${endHeight}px`, opacity: 0, transform: 'translateY(-8px)' }
      ],
      { duration, easing }
    );

    this.#animation.onfinish = () => this.#onAnimationFinish(false);
    this.#animation.oncancel = () => (this.#isClosing = false);
  }

  #onAnimationFinish(isOpen) {
    this.details.open = isOpen;
    if (isOpen) {
      this.details.setAttribute('open', '');
      this.details.removeAttribute('data-closing');
    } else {
      this.details.removeAttribute('open');
      this.details.removeAttribute('data-closing');
    }

    const content = this.content;
    if (content) {
      content.style.height = '';
      content.style.opacity = '';
      content.style.transform = '';
    }
    this.details.style.overflow = '';
    this.#animation = null;
    this.#isClosing = false;
    this.#isOpening = false;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      if (this.details.open) {
        this.close();
      }
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
